import { DataTableColumn } from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor, capitalizeFirst } from '../../utils';
import { LeverageRecord } from '../../Interfaces/mt5Interface';

const formatLeverage = (val: string | number | null | undefined): string => {
  if (val === null || val === undefined || String(val).trim() === '') {
    return '-';
  }
  let str = String(val).trim();
  if (str.startsWith('1:')) {
    str = str.substring(2);
  }
  return str || '-';
};

const getDisplayGroup = (row: LeverageRecord): string => {
  const rawGroup = row.account?.group || row.group || row.accountType || '';
  if (!rawGroup) return '-';
  const parts = rawGroup.split(/[/\\]/);
  return parts[0] || rawGroup;
};

const getChangedByName = (row: LeverageRecord): string => {
  if (row.changedBy && typeof row.changedBy === 'object') {
    return row.changedBy.fullName || row.changedBy.email || '-';
  }
  if (typeof row.changedBy === 'string' && row.changedBy.trim()) {
    return row.changedBy;
  }
  if (typeof row.requestedBy === 'string' && row.requestedBy.trim()) {
    return row.requestedBy;
  }
  if (row.account?.name) {
    return row.account.name;
  }
  return 'System';
};

export const getLeverageColumns = (
  handleViewDetails: (recordId: string) => void,
  handleEditLeverage?: (record: LeverageRecord) => void
): DataTableColumn<LeverageRecord>[] => [
  {
    id: 'mt5Login',
    header: 'MT5 Login',
    accessor: row => {
      const accNo = row.mt5Login || row.account?.mt5Login || '-';
      return <span className="font-mono text-sm font-semibold">{accNo}</span>;
    },
    sortable: true,
    sortValue: row => String(row.mt5Login || row.accountNumber || ''),
  },
  {
    id: 'accountName',
    header: 'Account Name',
    accessor: row => {
      const accountName = row.account?.name || '-';
      return (
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {accountName}
        </span>
      );
    },
    sortable: true,
    sortValue: row => row.account?.name || '',
  },
  {
    id: 'group',
    header: 'Group',
    accessor: row => {
      const displayGroup = getDisplayGroup(row);
      return (
        <Badge className={getStatusColor(displayGroup, 'account-type')}>
          {displayGroup}
        </Badge>
      );
    },
    sortable: true,
    sortValue: row => getDisplayGroup(row),
  },
  {
    id: 'oldLeverage',
    header: 'Old Leverage',
    width: '120px',
    accessor: row => (
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {formatLeverage(row.oldLeverage ?? row.previousLeverage)}
      </span>
    ),
    sortable: true,
    sortValue: row => String(row.oldLeverage ?? row.previousLeverage ?? ''),
  },
  {
    id: 'newLeverage',
    header: 'New Leverage',
    width: '120px',
    accessor: row => (
      <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
        {formatLeverage(row.newLeverage)}
      </span>
    ),
    sortable: true,
    sortValue: row => String(row.newLeverage ?? ''),
  },
  {
    id: 'status',
    header: 'Status',
    accessor: row => {
      const status = (row.status || 'success').toLowerCase();
      return (
        <Badge className={getStatusColor(status, 'status')}>
          {capitalizeFirst(status)}
        </Badge>
      );
    },
    sortable: true,
    sortValue: row => row.status || '',
  },
  {
    id: 'name',
    header: 'Updated By',
    accessor: row => {
      const name = getChangedByName(row);
      return (
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {name}
        </span>
      );
    },
    sortable: true,
    sortValue: row => getChangedByName(row),
  },
  {
    id: 'changeDate',
    header: 'Date & Time',
    accessor: row => (
      <div className="flex flex-col">
        <span className="text-sm font-medium">{row.changeDate || '-'}</span>
        <span className="text-xs text-gray-500">{row.changeTime || '-'}</span>
      </div>
    ),
    sortable: true,
    sortValue: row => row.changeDate || '',
  },
  {
    id: 'actions',
    header: 'Actions',
    accessor: row => (
      <ActionDropdown
        id={String(row.id)}
        showView={true}
        showEdit={Boolean(handleEditLeverage)}
        showDelete={false}
        onView={() => handleViewDetails(String(row.id || row.mt5Login))}
        onEdit={handleEditLeverage ? () => handleEditLeverage(row) : undefined}
      />
    ),
  },
];

export default getLeverageColumns;
